import { AlertContextType } from "../contexts/AlertContext";
import axios, { AxiosError } from "axios";

/**
 * 3 distinct error cases:
 * case 1. axios error with a `.response`: ApiError, the server responds with status code
 * case 2. axios without `.reponse`: network failure
 * case 3. plain js `Error`: created/bug in frontend code
 */
type ErrorType = AxiosError | Error;

interface BackendErrorBody {
  detail?: string;
  message?: string;
}

// convert unknown error to ErrorType
export const toErrorType = (error: unknown): ErrorType => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  return new Error("An unknown error occurred");
};

//all utility functions related to warnings
export const createAlertHelpers = (alertMethods: AlertContextType) => {
  const { showAlertModal, showAlert, showConfirmModal } = alertMethods;

  //shows an error modal
  const showErrorModal = (
    text: string,
    title: string = "Fehler",
    closeButtonText: string = "Schließen",
  ) => {
    showAlertModal(text, title, closeButtonText);
  };

  //a generic http based handler with no business logic. This function is primarily to reduce code duplication
  const reportGenericError = (error: ErrorType, attemptedAction: string) => {
    // handle case 3: NO user facing modal, just log error for developers
    if (!axios.isAxiosError(error)) {
      console.error(`Unexpected error during: ${attemptedAction}`, error);
      return;
    }

    //case 2: network failure: no response reached
    if (!error.response) {
      showAlertModal(
        `Beim ${attemptedAction} ist ein Netzwerkfehler aufgetreten...`,
      );
      return;
    }

    //case 1
    switch (error.response.status) {
      case 401: //unauthorized
        showErrorModal(
          `Beim ${attemptedAction} ist ein Fehler aufgetreten: Sie sind nicht (mehr) eingeloggt. Bitte loggen Sie sich erneut ein.`,
        );
        break;
      case 500: //internal server error
        showErrorModal(
          `Beim ${attemptedAction} ist ein Fehler aufgetreten: Interner Server Fehler`,
        );
        break;
      default:
        showErrorModal(
          `Beim ${attemptedAction} ist ein unbekannter Fehler aufgetreten. HTTP Statuscode: ${error.response.status}`,
        );
    }
  };

  //Reports an error which occured when loading the patient list (shows an alert box and logs the error to console)
  const reportGetPatientListError = (error: ErrorType) => {
    reportGenericError(error, "Abrufen der Patientenliste");
    console.error("Patientenliste Fail: ", error);
  };

  //Reports an error which occured when loading the clinician list (shows an alert box and logs the error to console)
  const reportGetClinicianListError = (error: ErrorType) => {
    reportGenericError(error, "Abrufen der Clinician Liste");
    console.error("Clinician list Fail: ", error);
  };

  //Reports an error which occured when loading the diagnoses list (shows an alert box and logs the error to console)
  const reportGetDiagnosesListError = (error: ErrorType) => {
    reportGenericError(error, "Abrufen der Diagnosen Liste");
    console.error("Diagnoses list Fail: ", error);
  };

  //Reports an error which occured when setting a new password (shows an alert box and logs the error to console)
  const reportNewPasswordError = (error: ErrorType) => {
    reportGenericError(error, "Setzen eines neuen Passworts");
    console.error("New password fail: ", error);
  };

  //Reports an error which occured when enabling/disabling a clinician (shows an alert box and logs the error to console)
  const reportDisableEnableClinicianError = (error: ErrorType) => {
    reportGenericError(error, "Aktivieren/Deaktivieren des Clinician");
    console.error("New password fail: ", error);
  };

  //Reports an error which occured when getting the clinician reports (shows an alert box and logs the error to console)
  const reportGetClinicianReportsError = (error: ErrorType) => {
    reportGenericError(error, "Laden der Berichte");
  };

  //Reports an error which occured when setting a new password (shows an alert box and logs the error to console)
  const reportGetClinicListError = (error: ErrorType) => {
    reportGenericError(error, "Laden der Klinik Liste");
    console.error("Klinikliste fail: ", error);
  };

  //Reports an error which occured when loading the survey session report (shows an alert box and logs the error to console)
  const reportGetSurveySessionReportError = (error: ErrorType) => {
    reportGenericError(error, "Laden der Befragungssitzung");
    console.error("Report fail: ", error);
  };

  //Reports an error which occured when loading stw (shows an alert box and logs the error to console)
  const reportGetStwsError = (error: ErrorType) => {
    reportGenericError(error, "Laden der Survey Time Windows");
    console.error("STWS fail: ", error);
  };

  //Reports an error which occured when the survey session report (shows an alert box and logs the error to console)
  const reportGetPatientSurveyItemsError = (error: ErrorType) => {
    reportGenericError(error, "Laden der Umfrageelemente");
    console.error("Get Patient survey items fail: ", error);
  };

  //Reports an error which occured when loading the patient reports (shows an alert box and logs the error to console)
  const reportGetPatientReportsError = (error: ErrorType) => {
    reportGenericError(error, "Laden der Patientenberichte");
    console.error("Get Patient reports fail: ", error);
  };

  //Reports an error which occured when loading survey (shows an alert box and logs the error to console)
  const reportGetSurveyError = (error: ErrorType) => {
    reportGenericError(error, "Laden der Umfrage");
    console.error("Get Patient reports fail: ", error);
  };

  /**
   * Reports an error which occured when registering patient or clinician (shows an alert box and logs the error to console)
   * @param {*} error
   */
  const reportRegistError = (error: ErrorType) => {
    const attemptedAction = "Registrieren";
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      showErrorModal(
        `Beim Registrieren ist ein Fehler aufgetreten: Diese ID ist bereits vergeben.`,
      );
    } else if (axios.isAxiosError(error) && error.response?.data) {
      const data = error.response.data as BackendErrorBody;
      const backendMessage = data.detail ?? data.message;
      if (backendMessage) {
        showErrorModal(
          "Der Benutzername enthält unzulässige Zeichen wie % / \\ ; ?",
        );
      } else {
        showErrorModal(
          "Beim " +
            attemptedAction +
            " ist ein unbekannter Fehler aufgetreten. HTTP Statuscode: " +
            error.response.status,
        );
      }
    } else {
      reportGenericError(error, attemptedAction);
    }
    console.error("Regist fail: ", error);
  };

  /**
   * Reports an error which occured when registering an admin (shows an alert box and logs the error to console)
   * @param {*} error
   */
  const reportRegistAdminError = (error: ErrorType) => {
    const attemptedAction = "Admin Registrieren ";
    if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 409
    ) {
      showErrorModal(
        "Beim " +
          attemptedAction +
          " ist ein Fehler aufgetreten: In dieser Klinik existiert bereits ein Admin.",
      );
    } else if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 400
    ) {
      showErrorModal(
        "Beim " +
          attemptedAction +
          " ist ein Fehler aufgetreten: Der Benutzername darf keine Leer- oder Sonderzeichen enthalten.",
      );
    } else {
      reportGenericError(error, attemptedAction);
    }
    console.error("Regist admin fail: ", error);
  };

  /**
   * Reports an error which occured when transfering a patient (shows an alert box and logs the error to console)
   * @param {*} error
   */
  const reportTransferPatientError = (error: ErrorType) => {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      showErrorModal("Error 403 ");
    } else {
      reportGenericError(error, "Transferieren des Patienten");
    }
    console.error("Transfer patient fail: ", error);
  };

  /**
   * Reports an error which occured when editing a clinician (shows an alert box and logs the error to console)
   * @param {*} error
   */
  const reportEditClinicianError = (error: ErrorType) => {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      showErrorModal("Der Benutzername ist bereits vergeben. ");
    } else if (axios.isAxiosError(error) && error.response?.data) {
      const data = error.response.data as BackendErrorBody;
      const backendMessage = data.detail ?? data.message;
      if (backendMessage) {
        showErrorModal(
          "Der Benutzername enthält unzulässige Zeichen wie % / \\ ; ?",
        );
      }
    } else {
      reportGenericError(error, "Editieren des Clinician");
    }
    console.error("Edit Clinician fail: ", error);
  };

  /**
   * Reports an error which occured when assigning a clinician (shows an alert box and logs the error to console)
   * @param {*} error
   */
  const reportClinicianAssignError = (error: ErrorType) => {
    reportGenericError(error, "Zuweisen");
    console.error("Assign Clinician fail: ", error);
  };

  /**
   * Reports an error which occured when editing a patient (shows an alert box and logs the error to console)
   * @param {*} error
   */
  const reportEditPatientError = (error: ErrorType) => {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      showErrorModal(
        "Beim Editieren des Patienten ist ein Fehler aufgetreten: Bitte überprüfen Sie die eingegebenen Daten (z.B. Geburtsdatum, Aufnahme- und Entlassdatum).",
      );
    } else {
      reportGenericError(error, "Editieren des Patienten");
    }
    console.error("Edit Patient fail: ", error);
  };

  /**
   * Reports an error which occured when submitting the survey (shows an alert box and logs the error to console)
   * @param {*} error
   */
  const reportSubmitSurveyError = (error: ErrorType) => {
    reportGenericError(error, "Übermitteln der Umfrage");
    console.error("Submit survey fail: ", error);
  };

  /**
   * Reports an error which occured when validating the csv (shows an alert box and logs the error to console)
   * @param {*} error
   */
  const reportCsvValidationError = (error: ErrorType) => {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      showErrorModal(
        "Bei der CSV Validation ist ein Fehler aufgetreten. Die CSV Datei besitzt nicht die korrekte Zeichenkodierung. Sie muss UTF-8 kodiert sein.",
      );
    } else if (axios.isAxiosError(error) && error.response?.status === 406) {
      showErrorModal(
        "Bei der CSV Validation ist ein Fehler aufgetreten. Die CSV Datei darf nicht UTF-8 BOM kodiert sein. Sie muss UTF-8 kodiert sein.",
      );
    } else if (axios.isAxiosError(error) && error.response?.status === 415) {
      showErrorModal(
        "Bei der Validierung ist ein Fehler aufgetreten. Die Datei hat das falsche Format. Das Format muss CSV sein.",
      );
    } else if (axios.isAxiosError(error) && error.response?.status === 413) {
      showErrorModal(
        "Bei der Validierung ist ein Fehler aufgetreten. Die Datei ist zu groß.",
      );
    } else {
      reportGenericError(error, "Validieren der CSV Datei");
    }
    console.error("Validation fail: ", error);
  };

  const showPasswordModal = (password: string) => {
    showAlertModal(
      "Das Passwort wurde erfolgreich zurückgesetzt. Das neue Passwort lautet: " +
        password +
        "\nBitte geben Sie das Passwort an den Benutzer weiter. \nSie können das Passwort zu einem späteren Zeitpunkt nicht mehr einsehen.",
      "Passwort",
      "Schließen",
    );
  };

  const showNewClinicianPasswordModal = (password) => {
    showAlertModal(
      `Das Passwort lautet: ${password}\n\nBitte geben Sie das Passwort an den Benutzer weiter. \nSie können das Passwort zu einem späteren Zeitpunkt nicht mehr einsehen.`,
      "Passwort",
      "Schließen",
    );
  };

  // shows the alert modal when non consent is given
  const showNonConsentModal = () => {
    showAlertModal(
      "Diese Person hat online kein Einverständnis zur Teilnahme an SysDok gegeben, daher liegen keine weiteren Daten oder Berichte vor. Sollte die Teilnahme versehentlich verweigert worden sein, wenden Sie sich bitte an Ihren Admin und lassen Sie die Person erneut anlegen.",
      "Keine Fragebogendaten vorhanden",
    );
  };

  /**
   * Shows an alertbox-like dialog to let the user know about an error during login as a patient
   * @param {string} authResponse  one of 'success' | 'credentials_wrong' | 'no_permissions' | 'error'
   */
  const reportPatientLoginError = (authResponse) => {
    if (authResponse === "credentials_wrong") {
      showErrorModal(
        "Ihre Anmeldedaten sind fehlerhaft. Bitte überprüfen Sie diese nochmal.",
      );
    } else if (authResponse === "no_permissions") {
      showErrorModal("Sie haben für diese Seite keine Berechtigung.");
    } else {
      showErrorModal("Beim Login ist ein Fehler aufgetreten.");
    }
  };

  const reportNoClinicSelected = () => {
    showErrorModal("Keine Klinik ausgewählt");
  };

  return {
    showErrorModal,
    showConfirmModal,
    showAlertModal,
    showAlert,
    reportGenericError,
    reportGetPatientListError,
    reportGetClinicianListError,
    reportGetDiagnosesListError,
    reportNewPasswordError,
    reportDisableEnableClinicianError,
    reportGetClinicianReportsError,
    reportGetClinicListError,
    reportGetSurveySessionReportError,
    reportGetStwsError,
    reportGetPatientSurveyItemsError,
    reportGetPatientReportsError,
    reportGetSurveyError,
    reportRegistError,
    reportRegistAdminError,
    reportTransferPatientError,
    reportEditClinicianError,
    reportClinicianAssignError,
    reportEditPatientError,
    reportSubmitSurveyError,
    reportCsvValidationError,
    showPasswordModal,
    showNonConsentModal,
    showNewClinicianPasswordModal,
    reportPatientLoginError,
    reportNoClinicSelected,
  };
};
